import React, { useState, useEffect } from 'react';
import { api } from './App';

const SOURCES = ['BARBRI', 'Law Drills'];

function AccuracyBar({ correct, attempted, height = 5 }) {
  const pct = attempted > 0 ? Math.round((correct / attempted) * 100) : 0;
  const color = pct >= 70 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height, background: 'var(--border2)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: pct + '%', height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color, minWidth: 36 }}>{pct}%</span>
      <span style={{ fontSize: 11, color: 'var(--muted)' }}>{correct}/{attempted}</span>
    </div>
  );
}

function BlendedBadge({ blended, self_conf, sessions }) {
  if (blended === null || blended === undefined) return null;
  const pct = Math.round(blended * 100);
  const color = pct >= 70 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
  const weight = sessions >= 6 ? '70% objective' : sessions >= 3 ? '50/50' : sessions >= 1 ? '30% objective' : 'self only';
  return (
    <span className="blended-badge" style={{ color, borderColor: color + '44', background: color + '11' }}
      title={`Blended score: ${weight} weighting across ${sessions} session${sessions !== 1 ? 's' : ''}`}>
      ⬡ {pct}% blended
    </span>
  );
}

function ParsedPreview({ results, allTopics, onSave, onDiscard }) {
  const [edited, setEdited] = useState(results.map(r => ({ ...r, include: true })));

  // Group allTopics for the selector dropdown
  const topicOptions = allTopics ? [
    { id: null, label: '— unmatched —' },
    ...allTopics.map(t => ({
      id: t.id,
      label: `[${t.module}] ${t.subtopic} (${t.topic})`,
      module: t.module,
    }))
  ] : [];

  const modules = ['FLK1', 'FLK2'];
  const [moduleFilter, setModuleFilter] = useState({ FLK1: true, FLK2: true });

  const getTopicModule = (topicId) => {
    if (!topicId || !allTopics) return null;
    const t = allTopics.find(t => t.id === topicId);
    return t ? t.module : null;
  };

  const visibleRows = edited.filter(r => {
    const mod = getTopicModule(r.topic_id);
    if (!mod) return true; // unmatched rows always show
    return moduleFilter[mod];
  });

  const toggleAll = (include) => setEdited(prev => prev.map(r => ({ ...r, include })));
  const includedCount = edited.filter(r => r.include).length;

  return (
    <div className="scraper-preview">
      <div className="scraper-preview-title">
        Review & reconcile — {includedCount} of {edited.length} selected to import
      </div>

      <div className="reconcile-toolbar">
        <div className="reconcile-module-filters">
          <span style={{ fontSize: 11, color: 'var(--muted)', marginRight: 6 }}>Show:</span>
          {modules.map(mod => (
            <button key={mod}
              className={`source-btn ${moduleFilter[mod] ? 'active' : ''}`}
              style={{ fontSize: 11, padding: '3px 10px' }}
              onClick={() => setModuleFilter(prev => ({ ...prev, [mod]: !prev[mod] }))}>
              {mod}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="reconcile-select-btn" onClick={() => toggleAll(true)}>Select all</button>
          <button className="reconcile-select-btn" onClick={() => toggleAll(false)}>Deselect all</button>
        </div>
      </div>

      <div className="scraper-table">
        <div className="reconcile-table-head">
          <span>✓</span>
          <span>Label from import</span>
          <span>Tracker subtopic match</span>
          <span>Score</span>
        </div>
        {visibleRows.map((r, vi) => {
          const i = edited.indexOf(r);
          const mod = getTopicModule(r.topic_id);
          const pct = r.score_pct != null ? r.score_pct : null;
          const pctColor = pct == null ? 'var(--muted)' : pct >= 70 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
          return (
            <div key={vi} className={`reconcile-table-row ${!r.include ? 'reconcile-row-excluded' : ''}`}>
              <input type="checkbox" checked={r.include}
                onChange={e => setEdited(prev => prev.map((x, j) => j === i ? { ...x, include: e.target.checked } : x))} />
              <div className="scraper-topic-cell">
                <span className="scraper-topic-name">{r.topic_name}</span>
                {r.matched_subtopic && r.matched_subtopic !== r.topic_name && (
                  <span style={{ fontSize: 10, color: 'var(--muted)', display: 'block' }}>→ {r.matched_subtopic}</span>
                )}
                {mod && <span className={`scraper-module-tag ${mod === 'FLK1' ? 'tag-flk1' : 'tag-flk2'}`} style={{ marginTop: 3 }}>{mod}</span>}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <select className="reconcile-topic-select"
                  value={r.topic_id || ''}
                  onChange={e => {
                    const val = e.target.value ? parseInt(e.target.value) : null;
                    setEdited(prev => prev.map((x, j) => j === i ? { ...x, topic_id: val } : x));
                  }}>
                  {topicOptions.map(opt => (
                    <option key={opt.id || 'null'} value={opt.id || ''}>{opt.label}</option>
                  ))}
                </select>
                <span style={{ fontSize: 10, color: r.topic_id ? 'var(--green)' : 'var(--red)', paddingLeft: 4 }}>
                  {r.topic_id ? '✓ matched' : '! needs mapping — use dropdown above'}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 18, fontWeight: 500, color: pctColor }}>
                  {pct != null ? pct + '%' : '—'}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      <div className="scraper-actions">
        <button className="scraper-save-btn"
          onClick={() => onSave(edited.filter(r => r.include && r.score_pct != null))}>
          Confirm &amp; import {includedCount} result{includedCount !== 1 ? 's' : ''}
        </button>
        <button className="scraper-discard-btn" onClick={onDiscard}>Discard</button>
      </div>
    </div>
  );
}

export default function MCQScraper() {
  const [source, setSource] = useState('BARBRI');
  const [pastedText, setPastedText] = useState('');
  const [allTopics, setAllTopics] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [rawForSave, setRawForSave] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [streak, setStreak] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);

  const loadSummary = () => {
    api('/api/mcq-results/summary').then(data => { setSummary(data); setLoadingSummary(false); });
    api('/api/mcq-streak').then(setStreak);
    api('/api/mcq-results/sessions').then(setSessions);
  };

  useEffect(() => { loadSummary(); }, [saved]);

  const deleteSession = async (source, session_date) => {
    if (!window.confirm(`Delete the ${source} session from ${session_date}? This cannot be undone.`)) return;
    await api('/api/mcq-results/delete-session', {
      method: 'POST',
      body: JSON.stringify({ source, session_date }),
    });
    setSaved(s => !s);
  };

  const [saving, setSaving] = useState(false);

  const handleParse = async () => {
    if (!pastedText.trim() || parsing) return;
    setParsing(true); setError('');
    try {
      const res = await api('/api/mcq-results/parse', {
        method: 'POST',
        body: JSON.stringify({ text: pastedText, source }),
      });
      if (res.error) setError(res.error);
      else { setParsed(res.results); setAllTopics(res.all_topics || []); setRawForSave(pastedText); }
    } catch (e) { setError('Failed to parse. Check server logs.'); }
    setParsing(false);
  };

  const handleSave = async (results) => {
    if (saving) return;
    setSaving(true);
    const res = await api('/api/mcq-results/save', {
      method: 'POST',
      body: JSON.stringify({ results, raw: rawForSave, source }),
    });
    setSaving(false);
    if (res.ok === false) {
      setError(res.error || 'Failed to save');
      return;
    }
    setParsed(null); setPastedText(''); setError(''); setSaved(s => !s);
  };

  const daysSinceImport = streak?.days_since_import;
  const importWarning = daysSinceImport >= 7;

  return (
    <div className="scraper-page">
      <div className="scraper-intro">
        <div className="scraper-intro-title">MCQ Results import</div>
        <p className="scraper-intro-text">
          After a MCQ session, go to your results page, press <kbd>Ctrl+A</kbd> then <kbd>Ctrl+C</kbd>, paste below.
          Claude will parse which topics you covered and your score. Results from all sources feed into the blended confidence score.
        </p>
      </div>

      {streak && (
        <div className={`streak-bar ${importWarning ? 'streak-warning' : 'streak-ok'}`}>
          {importWarning
            ? `⚠ Last import was ${daysSinceImport} days ago — import your latest results to keep scores accurate`
            : `✓ Last import ${daysSinceImport === 0 ? 'today' : `${daysSinceImport} days ago`} · ${streak.total_imports} session${streak.total_imports !== 1 ? 's' : ''} logged`}
        </div>
      )}

      {!parsed && (
        <div className="scraper-input-section">
          <div className="source-picker">
            {SOURCES.map(s => (
              <button key={s} className={`source-btn ${source === s ? 'active' : ''}`}
                onClick={() => setSource(s)}>
                {s}
              </button>
            ))}
          </div>
          <textarea className="scraper-textarea"
            placeholder={`Paste your ${source} results here…`}
            value={pastedText} onChange={e => setPastedText(e.target.value)} />
          {error && <div className="scraper-error">{error}</div>}
          <button className="scraper-parse-btn" onClick={handleParse} disabled={parsing || !pastedText.trim()}>
            {parsing ? 'Parsing with Claude…' : `Parse ${source} results`}
          </button>
        </div>
      )}

      {parsed && <ParsedPreview results={parsed} allTopics={allTopics} onSave={handleSave} onDiscard={() => setParsed(null)} />}

      <div className="scraper-summary-section">
        <div className="scraper-summary-title">Per-topic accuracy & blended confidence</div>
        <div className="blended-explainer">
          The blended score combines self-assessed confidence with objective MCQ accuracy.
          More sessions = more weight on objective results.
          <span className="blended-weights"> 1–2 sessions: 30% objective · 3–5: 50/50 · 6+: 70% objective</span>
        </div>
        {!loadingSummary && sessions.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: 12, color: 'var(--muted)' }}>Import sessions ({sessions.length})</span>
              <button className="reconcile-select-btn" onClick={() => setShowSessions(s => !s)}>
                {showSessions ? 'Hide sessions' : 'Manage sessions'}
              </button>
            </div>
            {showSessions && (
              <div className="session-manager">
                {sessions.map((s, i) => {
                  const pct = s.total_attempted > 0 ? Math.round(s.total_correct / s.total_attempted * 100) : 0;
                  const color = pct >= 70 ? 'var(--green)' : pct >= 50 ? 'var(--amber)' : 'var(--red)';
                  const dateStr = (() => { try { return new Date(s.session_date).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); } catch { return s.session_date; } })();
                  return (
                    <div key={i} className="session-row">
                      <div className="session-info">
                        <span className="session-source">{s.source}</span>
                        <span className="session-date">{dateStr}</span>
                        <span className="session-topics">{s.topic_count} topics</span>
                        <span className="session-score" style={{ color }}>{pct}% overall</span>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                        <button className="session-delete-btn" style={{ color: 'var(--accent)', borderColor: 'var(--accent)' }}
                          onClick={async () => {
                            const res = await api(`/api/mcq-results/raw/${encodeURIComponent(s.source)}/${s.session_date}`);
                            if (res.ok && res.raw_text) {
                              setSource(res.source);
                              setPastedText(res.raw_text);
                              setShowSessions(false);
                              window.scrollTo(0, 0);
                            } else {
                              alert('Raw text not available for this session — paste manually.');
                            }
                          }}>
                          ↩ Re-import
                        </button>
                        <button className="session-delete-btn" onClick={() => deleteSession(s.source, s.session_date)}>
                          🗑 Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                  <button className="scraper-discard-btn" style={{ fontSize: 11, color: 'var(--red)', borderColor: 'var(--red)' }}
                    onClick={async () => {
                      if (!window.confirm('Delete ALL MCQ results? This cannot be undone.')) return;
                      await api('/api/mcq-results/clear', { method: 'POST', body: JSON.stringify({}) });
                      setSaved(s => !s);
                    }}>
                    🗑 Delete all sessions
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      {loadingSummary ? (
          <div className="loading">Loading…</div>
        ) : summary.length === 0 ? (
          <div className="scraper-empty">No MCQ results logged yet. Import a session above to get started.</div>
        ) : (
          <div className="scraper-table">
            <div className="scraper-table-head scraper-table-head-wide">
              <span>Topic</span>
              <span>Sessions</span>
              <span>MCQ accuracy</span>
              <span>Self-assessed</span>
              <span>Blended</span>
              <span>Last session</span>
            </div>
            {summary.map(r => {
              const selfPct = r.self_confidence !== null ? Math.round(r.self_confidence * 100) : null;
              const selfColor = selfPct === null ? 'var(--muted)' : selfPct < 50 ? 'var(--red)' : selfPct < 70 ? 'var(--amber)' : 'var(--green)';
              return (
                <div key={r.topic_id} className="scraper-table-row scraper-table-row-wide">
                  <div>
                    <span className="scraper-topic-name">{r.topic_name}</span>
                    {r.module && <span className={`scraper-module-tag ${r.module === 'FLK1' ? 'tag-flk1' : 'tag-flk2'}`}>{r.module}</span>}
                  </div>
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--muted)' }}>{r.sessions}</span>
                  <AccuracyBar correct={r.total_correct} attempted={r.total_attempted} />
                  <span style={{ fontFamily: 'var(--mono)', fontSize: 12, color: selfColor }}>
                    {selfPct !== null ? selfPct + '%' : '—'}
                  </span>
                  <BlendedBadge blended={r.blended_confidence} self_conf={r.self_confidence} sessions={r.sessions} />
                  <span style={{ fontSize: 11, color: 'var(--muted)' }}>{(() => {
                  try { const d = new Date(r.last_session); return d.toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); } catch { return r.last_session; }
                })()}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
